import { useSearchParamsState } from 'hooks/useSearchParamState';
import { useStore } from 'hooks/useStore';
import {
  DropdownDetails,
  OpportunityDetail,
  PortfolioOpportunitiesDetailsInterface,
  Stage,
} from 'Interfaces/BDPortalInterfaces';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bdportalScreenServices } from 'services/bdportalScreenServices';
import { deepCopy } from 'utils/deepCopy';

const PortfolioOpportuntiesListScreenLogic = () => {
  const {
    UserStore: { userModulesRoles, userMetadata },
    BDPortalStore: {
      board,
      setBoard,
      filterAnalyst,
      filterAccManagers,
      addInQuickLoad,
      removeFromQuickLoad,
      allOpportunityDetails,
      // allOpportunityDetails,
      searchKeyword,
      filterStage,
      filterStatus,
      quickLoadCards,
      getDropdownDetails,
      dropdownDetails,
      filterPrimarySupplier,
      filterSource,
      filterAgency,
      filterIsPortfolioOpportunity,
      filterAnnualRevenueRange,
      filterStartAndEnddates,
      // setAllOpportunityDetails,
    },
  } = useStore();
  const [boardDetails, setBoardDetails] = useState<DropdownDetails | null>(null);
  const [portfolioOpportunityDetails, setPortfolioOpportunityDetails] = useState<
    PortfolioOpportunitiesDetailsInterface[]
  >([]);
  const [iteration, setIteration] = useState<string>(board);
  const [stageNames, setStageNames] = useState<string[]>([]);
  const [isEditOpportunityModalOpen, setIsEditOpportunityModalOpen] = useState(false);
  const [expandedPortfolioStages, setExpandedPortfolioStages] = useState<Set<number>>(new Set());
  const [opportunityIID, setOpportunityIID] = useState(0);
  const [isDropDownDetailsAndOppLoading, setIsDropDownDetailsAndOppLoading] = useState(false);
  const [opportunityID, setOpportunityID] = useState<number | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIterationIds, setSelectedIterationIds] = useState<Set<number>>(new Set());
  const [opportunityIdParam] = useSearchParamsState(
    'opportunityIdParam',
    ''
  );
  const [opportunityIterationIdParam] = useSearchParamsState(
    'opportunityIterationIdParam',
    ''
  );
  const [portfolioTabStatusStage, setPortfolioTabStatusStage] = useState<
    Map<number, { status: number | null; stage: number | null }>
  >(new Map());

  const [portfolioTableUpdating, setPortfolioTableUpdating] = useState<Set<number>>(new Set());

  const handleGetDropdownDetails = useCallback(async () => {
    try {
      const response = await getDropdownDetails();
      setBoardDetails(response!);
      if (response) {
        if (board === 'NC' && response.NC) {
          setStageNames(response.NC.Stage.map((stageObj: Stage) => stageObj.Stage));
        } else if (board === 'MAS' && response.MAS) {
          setStageNames(response.MAS.Stage.map((stageObj: Stage) => stageObj.Stage));
        } else {
          setStageNames([]);
        }
      }
    } catch {
      // Error fetching dropdown details - handled silently
    }
  }, [board, getDropdownDetails]);

  useEffect(() => {
    handleGetDropdownDetails();
    // if both the params are in the url open the edit opportunity modal
    if (opportunityIdParam && opportunityIterationIdParam) {
      setOpportunityID(parseInt(opportunityIdParam));
      setOpportunityIID(parseInt(opportunityIterationIdParam));
      setIsEditOpportunityModalOpen(true);
    }
  }, [handleGetDropdownDetails, opportunityIdParam, opportunityIterationIdParam]);

  const fetchDropdownDetails = useCallback(async (signal: AbortSignal | null = null) => {
    setIsDropDownDetailsAndOppLoading(true);
    try {
      await getDropdownDetails(signal || undefined);
      const response = await bdportalScreenServices.getAllPortfolioOpportunities(
        board,
        signal ? signal : undefined
      );
      setPortfolioOpportunityDetails(response);
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'CanceledError') {
        // API call was aborted - no action needed
      } else if (error instanceof Error) {
        toast.error(error.message);
      }
    } finally {
      setIsDropDownDetailsAndOppLoading(false);
    }
  }, [board, getDropdownDetails]);

  useEffect(() => {
    if (portfolioOpportunityDetails.length > 0) {
      setExpandedPortfolioStages(new Set());
    }
    const controller = new AbortController();
    const { signal } = controller;

    fetchDropdownDetails(signal);

    return () => {
      controller.abort();
    };
  }, [board, allOpportunityDetails, fetchDropdownDetails, portfolioOpportunityDetails.length]);

  useEffect(() => {
    if (expandedPortfolioStages.size > 0) return;
    if (portfolioOpportunityDetails.length > 9) {
      setIteration(board);
      setExpandedPortfolioStages(new Set([portfolioOpportunityDetails[0].PortfolioCounter]));
    } else {
      setExpandedPortfolioStages(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageNames, portfolioOpportunityDetails, board]);


  const closeEditOpportunityModal = (isNewIterationCreated: boolean = false) => {
    setIsEditOpportunityModalOpen(false);
    // if new iteration created fetch the board details again
    if (isNewIterationCreated) {
      fetchDropdownDetails();
    }
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('opportunityIdParam');
    newSearchParams.delete('opportunityIterationIdParam');
    newSearchParams.delete('isIterationOpen');
    setSearchParams(newSearchParams);
  };

  const handleSetItertation = (iteration: string) => {
    setIteration(iteration);
    setBoard(iteration);
  };

  const handleListButtonClick = () => {
    // navigation logi
    navigate('bdportal/optracker/list');
  };

  const handleCardClick = async (opportunityIID: number, opportunityID: number) => {
    setIsEditOpportunityModalOpen(true);
    setOpportunityIID(opportunityIID);
    setOpportunityID(opportunityID);
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('opportunityIdParam', opportunityID.toString());
    newSearchParams.set('opportunityIterationIdParam', opportunityIID.toString());
    setSearchParams(newSearchParams);
  };


  const isValidMove = (
    currentStage: number,
    newStage: number,
    _currentStatus: number,
    _newStatus: number,
    board: string
  ): boolean => {
    if (!userMetadata) return false;

    if (
      userMetadata?.userInfo.deptId.departmentName.toLocaleLowerCase() === 'developer' ||
      userMetadata?.userInfo.deptId.departmentName.toLocaleLowerCase() === 'management'
    ) {
      return true;
    }

    if (userMetadata?.roles.opptracker.length === 0) {
      return false;
    }

    if (
      userMetadata.roles.opptracker[0].roleId.roleCode.toLocaleLowerCase() === 'admin' ||
      userMetadata.roles.opptracker[0].roleId.roleCode.toLocaleLowerCase() === 'super'
    ) {
      return true;
    }

    if (currentStage === newStage) {
      return true;
    }

    if (board === 'NC' || board === 'MAS') {
      if (newStage <= 3 && currentStage <= 3) {
        return true;
      }
    }

    return false;
  };

  const handleDropdownChange = async (
    index: number,
    opportunityID: number,
    field: 'Stage' | 'Status' | 'Analyst',
    newValue: number
  ) => {
    const allOpportunityDetails = portfolioOpportunityDetails[index].opportunities;

    const movedOpportunity = allOpportunityDetails.find(
      opportunity => opportunity.OpportunityIterationsID === opportunityID
    );

    if (!movedOpportunity) return;

    const originalOpportunities = [...allOpportunityDetails];
    const currentStage = movedOpportunity.StageID;
    const currentStatus = movedOpportunity.StatusID;
    const currentAnalyst = movedOpportunity.AnalystID;

    const originalValue =
      field === 'Stage' ? currentStage : field === 'Status' ? currentStatus : currentAnalyst;

    if (originalValue === newValue) return;

    if (
      (field === 'Stage' && !isValidMove(currentStage, newValue, currentStatus, 1, board)) ||
      (field === 'Status' &&
        !isValidMove(currentStage, currentStage, currentStatus, newValue, board))
    ) {
      toast.error('Invalid move, requires admin role', {
        position: 'bottom-right',
        autoClose: 5000,
      });
      return;
    }
    addInQuickLoad(opportunityID);

    const tempPortfolioOpportunity = deepCopy(portfolioOpportunityDetails);

    // checking if the move is from or to Go/No-Go stage
    if (field === 'Stage') {
      if ((currentStage <= 7 && newValue === 8) || (currentStage === 8 && newValue <= 7)) {
        tempPortfolioOpportunity[index].opportunities = tempPortfolioOpportunity[
          index
        ].opportunities.map(opportunity =>
          opportunity.OpportunityIterationsID === opportunityID
            ? {
                ...opportunity,
                StatusID: 1,
              }
            : opportunity
        );
      }
    }

    try {
      // const tempPortfolioOpportunity = deepCopy(portfolioOpportunityDetails);

      tempPortfolioOpportunity[index].opportunities = tempPortfolioOpportunity[
        index
      ].opportunities.map(opportunity =>
        opportunity.OpportunityIterationsID === opportunityID
          ? {
              ...opportunity,
              [field === 'Stage' ? 'StageID' : field === 'Status' ? 'StatusID' : 'AnalystID']:
                newValue,
              // ...(field === "Stage" && { StatusID: 1 }),
            }
          : opportunity
      );

      setPortfolioOpportunityDetails(tempPortfolioOpportunity);

      const payload = {
        OpportunityIterationsID: opportunityID,
        StageID: field === 'Stage' ? newValue : currentStage,
        StatusID: field === 'Status' ? newValue : currentStatus,
        AnalystID: field === 'Analyst' ? newValue : currentAnalyst,
      };

      await bdportalScreenServices.updateBoard(payload, board);
    } catch (error: unknown) {
      const tempPortfolioOpportunity = deepCopy(portfolioOpportunityDetails);

      tempPortfolioOpportunity[index].opportunities = originalOpportunities;

      setPortfolioOpportunityDetails(tempPortfolioOpportunity);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Error updating ${field}: ${errorMessage}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      removeFromQuickLoad(opportunityID);
    }
  };

  const handleAccordionClick = (portfolioCounter: number) => {
    const tempExpandedPortfolioStages: Set<number> = new Set(expandedPortfolioStages);
    if (expandedPortfolioStages.has(portfolioCounter)) {
      tempExpandedPortfolioStages.delete(portfolioCounter);
    } else {
      tempExpandedPortfolioStages.add(portfolioCounter);
    }
    setExpandedPortfolioStages(tempExpandedPortfolioStages);
  };

  const getSourceNameForSourceId = (sourceId: number) => {
    let source = '-';
    (board === 'NC' ? dropdownDetails?.NC.Source : dropdownDetails?.MAS.Source)?.forEach(
      sourceInstance => {
        if (sourceInstance.ID === sourceId) {
          source = sourceInstance.Source;
        }
      }
    );
    return source;
  };

  const filterOpportunityIterationsForTheappliedFilters = (
    opportunitIterationsData: OpportunityDetail[]
  ) => {
    let filteredOpportunityIterations: OpportunityDetail[] = [];
    filteredOpportunityIterations = opportunitIterationsData.filter(iterationData => {
      // check if the curent iteratino passed the filtere criteria
      if (
        searchKeyword &&
        !iterationData.MoleculeAndForm.toLocaleLowerCase().includes(
          searchKeyword.toLocaleLowerCase()
        )
      ) {
        return false;
      }

      if (
        !(
          (!filterStage || iterationData.StageID === filterStage) &&
          (!filterStatus || iterationData.StatusID === filterStatus)
        )
      ) {
        return false;
      }

      // check for the unassigned filter
      if (
        filterAnalyst &&
        filterAnalyst.length > 0 &&
        filterAnalyst.indexOf(iterationData.AnalystID ? iterationData.AnalystID : 0) === -1
      ) {
        return false;
      }

      // check for the account managers filter
      if (
        filterAccManagers &&
        filterAccManagers.length > 0 &&
        filterAccManagers.indexOf(
          iterationData.AccountManagerID ? iterationData.AccountManagerID : 0
        ) === -1
      ) {
        return false;
      }

      // check if the opportunity type is portfolio opporunity or not
      if (
        filterIsPortfolioOpportunity !== null &&
        Boolean(iterationData.PortfolioCounter) !== filterIsPortfolioOpportunity
      ) {
        return false;
      }

      if (
        filterAnnualRevenueRange &&
        (filterAnnualRevenueRange[0] || filterAnnualRevenueRange[1])
      ) {
        if (
          !(
            (iterationData.EstAnnualRevenue || 0) >= filterAnnualRevenueRange[0] &&
            (iterationData.EstAnnualRevenue || 0) <= filterAnnualRevenueRange[1]
          )
        ) {
          return false;
        }
      }

      // start end dates range filter

      if (filterStartAndEnddates && filterStartAndEnddates[0] && filterStartAndEnddates[1]) {
        const startDate = new Date(filterStartAndEnddates[0] as Date);
        const endDate = new Date(filterStartAndEnddates[1] as Date);
        const createdAt = new Date(iterationData.CreatedAt);
        if (!(createdAt >= startDate && createdAt <= endDate)) {
          return false;
        }
      }

      // Check if source filter is applied and doesn't match the iteration data
      if (filterSource?.length !== 0 && filterSource?.indexOf(iterationData.SourceID) === -1) {
        return false;
      }

      // Check if primary filter is applied and doesn't match the iteration data
      if (
        filterPrimarySupplier &&
        !(
          iterationData.PrimarySupplierID?.toLocaleLowerCase() ===
          filterPrimarySupplier.toLocaleLowerCase()
        )
      ) {
        return false;
      }

      const agency = iterationData.Agency
        ? JSON.parse(iterationData.Agency.replace(/'/g, '"'))
        : [0];
      if (filterAgency && filterAgency.length > 0) {
        let found = false;
        agency.forEach((agencyInstance: number) => {
          if (filterAgency.indexOf(agencyInstance) !== -1) {
            found = true;
          }
        });
        if (!found) return false;
      }

      return true;
    });
    return filteredOpportunityIterations;
  };

  const handleClickSelectAllForPortfolioCounter = (portfolioCounter: number) => {
    const filteredOpportunityIterations = portfolioOpportunityDetails.find(
      opportunity => opportunity.PortfolioCounter === portfolioCounter
    );
    if (filteredOpportunityIterations) {
      let allPendingOpportunitiesSelected = true;
      // firstly check if all the opportunities are already select for the pending status
      filteredOpportunityIterations.opportunities.forEach(opportunity => {
        if (
          opportunity.StatusID === 1 &&
          !selectedIterationIds.has(opportunity.OpportunityIterationsID)
        ) {
          allPendingOpportunitiesSelected = false;
        }
      });

      const tempSelectedIterationIds = new Set(selectedIterationIds);

      if (!allPendingOpportunitiesSelected) {
        // then select all the opportunities
        filteredOpportunityIterations.opportunities.forEach(opportunity => {
          if (opportunity.StatusID === 1) {
            tempSelectedIterationIds.add(opportunity.OpportunityIterationsID);
          }
        });
      } else {
        // then deselect all the opportunities
        filteredOpportunityIterations.opportunities.forEach(opportunity => {
          tempSelectedIterationIds.delete(opportunity.OpportunityIterationsID);
        });
      }
      setSelectedIterationIds(tempSelectedIterationIds);
    }
  };

  const handleClickSelectSingleIteration = (opportunityIterationId: number) => {
    const tempSelectedIterationIds = new Set(selectedIterationIds);
    if (selectedIterationIds.has(opportunityIterationId)) {
      tempSelectedIterationIds.delete(opportunityIterationId);
    } else {
      tempSelectedIterationIds.add(opportunityIterationId);
    }
    setSelectedIterationIds(tempSelectedIterationIds);
  };

  const handleCheckIfAllPendingOpportunitiesAreSelected = (portfolioCounter: number) => {
    const filteredOpportunityIterations = portfolioOpportunityDetails.find(
      opportunity => opportunity.PortfolioCounter === portfolioCounter
    );
    if (filteredOpportunityIterations) {
      return filteredOpportunityIterations.opportunities.every(
        opportunity =>
          opportunity.StatusID !== 1 ||
          selectedIterationIds.has(opportunity.OpportunityIterationsID)
      );
    }
    return false;
  };

  const checkIfParticularIterationIsSelected = (opportunityIterationId: number) => {
    return selectedIterationIds.has(opportunityIterationId);
  };

  const checkIfSelectAllIsDisabled = (portfolioCounter: number) => {
    const filteredOpportunityIterations = portfolioOpportunityDetails.find(
      opportunity => opportunity.PortfolioCounter === portfolioCounter
    );
    if (filteredOpportunityIterations) {
      return filteredOpportunityIterations.opportunities.every(
        opportunity => opportunity.StatusID !== 1
      );
    }
  };

  const checkIfAnyIterationSelected = (portfolioCounter: number) => {
    const filteredOpportunityIterations = portfolioOpportunityDetails.find(
      opportunity => opportunity.PortfolioCounter === portfolioCounter
    );
    if (filteredOpportunityIterations) {
      return filteredOpportunityIterations.opportunities.some(opportunity =>
        selectedIterationIds.has(opportunity.OpportunityIterationsID)
      );
    }
    return false;
  };

  useEffect(() => {
    const tempPortfolioTabStatusStage = new Map<
      number,
      { status: number | null; stage: number | null }
    >();
    portfolioOpportunityDetails.forEach(opportunity => {
      tempPortfolioTabStatusStage.set(opportunity.PortfolioCounter, {
        status: null,
        stage: null,
      });
    });
    setPortfolioTabStatusStage(tempPortfolioTabStatusStage);
  }, [portfolioOpportunityDetails]);

  const handleUpdatePortfolioTabStatus = (portfolioCounter: number, status: number | null) => {
    const tempPortfolioTabStatusStage = new Map(portfolioTabStatusStage);
    tempPortfolioTabStatusStage.set(portfolioCounter, {
      status: status,
      stage: tempPortfolioTabStatusStage.get(portfolioCounter)?.stage || null,
    });
    setPortfolioTabStatusStage(tempPortfolioTabStatusStage);
  };

  const handleUpdatePortfolioTabStage = (portfolioCounter: number, stage: number | null) => {
    const tempPortfolioTabStatusStage = new Map(portfolioTabStatusStage);
    tempPortfolioTabStatusStage.set(portfolioCounter, {
      status: tempPortfolioTabStatusStage.get(portfolioCounter)?.status || null,
      stage: stage,
    });
    setPortfolioTabStatusStage(tempPortfolioTabStatusStage);
  };

  const getPortfolioTabStatus = (portfolioCounter: number) => {
    return portfolioTabStatusStage.get(portfolioCounter)?.status;
  };

  const getPortfolioTabStage = (portfolioCounter: number) => {
    return portfolioTabStatusStage.get(portfolioCounter)?.stage;
  };

  const handleSaveButtonClick = async (portfolioCounter: number) => {
    try {
      const tempPortfolioTableUpdating = new Set(portfolioTableUpdating);
      tempPortfolioTableUpdating.add(portfolioCounter);
      setPortfolioTableUpdating(tempPortfolioTableUpdating);
      const filteredOpportunityIterations = filterOpportunityIterationsForTheappliedFilters(
        portfolioOpportunityDetails.find(
          opportunity => opportunity.PortfolioCounter === portfolioCounter
        )?.opportunities || []
      );
      if (filteredOpportunityIterations) {
        // now we need to update the status and stage of the opportunities
        const portfoliTableUpdatePayload: {
          OpportunityIterationsID: number;
          StatusID: number;
          StageID: number;
        }[] = [];
        filteredOpportunityIterations.forEach(opportunity => {
          const payload = {
            OpportunityIterationsID: opportunity.OpportunityIterationsID,
            StatusID: selectedIterationIds.has(opportunity.OpportunityIterationsID)
              ? getPortfolioTabStatus(portfolioCounter) || 0
              : opportunity.StageID <= 7
                ? 3
                : 5,
            StageID: selectedIterationIds.has(opportunity.OpportunityIterationsID)
              ? getPortfolioTabStage(portfolioCounter) || 0
              : opportunity.StageID,
          };
          portfoliTableUpdatePayload.push(payload);
        });
        await bdportalScreenServices.bulkUpdateBoard(
          portfoliTableUpdatePayload,
          board
        );
        const tempPortfolioOpportunityDetails = deepCopy(portfolioOpportunityDetails);
        tempPortfolioOpportunityDetails.forEach(opportunity => {
          if (opportunity.PortfolioCounter === portfolioCounter) {
            opportunity.opportunities = opportunity.opportunities.map(opportunity => {
              return {
                ...opportunity,
                StatusID: selectedIterationIds.has(opportunity.OpportunityIterationsID)
                  ? getPortfolioTabStatus(portfolioCounter) || 0
                  : opportunity.StageID <= 7
                    ? 3
                    : 5,
                StageID: selectedIterationIds.has(opportunity.OpportunityIterationsID)
                  ? getPortfolioTabStage(portfolioCounter) || 0
                  : opportunity.StageID,
              };
            });
          }
        });

        const tempSelectedIterationIds = new Set(selectedIterationIds);

        filteredOpportunityIterations.forEach(opportunity => {
          if (selectedIterationIds.has(opportunity.OpportunityIterationsID)) {
            tempSelectedIterationIds.delete(opportunity.OpportunityIterationsID);
          }
        });
        setSelectedIterationIds(tempSelectedIterationIds);

        setPortfolioOpportunityDetails(tempPortfolioOpportunityDetails);
        const tempPortfolioTabStatusStage = new Map(portfolioTabStatusStage);
        tempPortfolioTabStatusStage.set(portfolioCounter, {
          status: null,
          stage: null,
        });
        setPortfolioTabStatusStage(tempPortfolioTabStatusStage);
        // console.log(response);
        toast.success('Successfully Bulk Updated !');
      }
    } catch {
      toast.error('Error updating portfolio table');
    } finally {
      const tempPortfolioTableUpdating = new Set(portfolioTableUpdating);
      tempPortfolioTableUpdating.delete(portfolioCounter);
      setPortfolioTableUpdating(tempPortfolioTableUpdating);
    }
  };

  const checkIfPortfolioTableUpdating = (portfolioCounter: number) => {
    return portfolioTableUpdating.has(portfolioCounter);
  };

  const checkIfPortfolioSaveButtonIsDisabled = (portfolioCounter: number) => {
    const portfolioTabStageStatusDetails = portfolioTabStatusStage.get(portfolioCounter);
    if (
      !checkIfAnyIterationSelected(portfolioCounter) ||
      portfolioTabStageStatusDetails?.stage === null ||
      portfolioTabStageStatusDetails?.status === null
    ) {
      return true;
    }
    return false;
  };

  return {
    userModulesRoles,
    isDropDownDetailsAndOppLoading,
    boardDetails,
    handleSetItertation,
    iteration,
    opportunityIID,
    isEditOpportunityModalOpen,
    closeEditOpportunityModal,
    handleListButtonClick,
    board,
    filterAnalyst,
    portfolioOpportunityDetails,
    searchKeyword,
    handleCardClick,
    filterStatus,
    filterStage,
    quickLoadCards,
    handleDropdownChange,
    opportunityID,
    handleAccordionClick,
    expandedPortfolioStages,
    getSourceNameForSourceId,
    filterOpportunityIterationsForTheappliedFilters,
    handleClickSelectAllForPortfolioCounter,
    handleClickSelectSingleIteration,
    handleCheckIfAllPendingOpportunitiesAreSelected,
    checkIfParticularIterationIsSelected,
    checkIfSelectAllIsDisabled,
    checkIfAnyIterationSelected,
    portfolioTabStatusStage,
    handleUpdatePortfolioTabStatus,
    handleUpdatePortfolioTabStage,
    getPortfolioTabStage,
    getPortfolioTabStatus,
    handleSaveButtonClick,
    checkIfPortfolioTableUpdating,
    checkIfPortfolioSaveButtonIsDisabled,
  };
};

export default PortfolioOpportuntiesListScreenLogic;
